import {
  GroupsSortBy,
  GroupsSortField,
  GroupTypes,
} from "@custom-types/group-types";
import { SortItem } from "@custom-types/sort-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { DEFAULT_GROUPS_SORT_BY } from "@components/groups/groups-constants";
import { isGroupDetails } from "@custom-types/type-guards";

/** Defines the minimum length for the group name */
export const MIN_GROUP_NAME_LENGTH = 1;

/** Returns true if a group should have a demo label. */
export function isGroupDemo(
  group: FaroCoreApiTypes.GroupSummaryResponse
): boolean {
  return group.name === "Demo";
}

/** Get number of projects in a group */
export function getProjectNumbersInGroup(group: GroupTypes): number {
  // TODO: This check can be removed once the backend removes "projects"
  // and adds "numProjects" to the GroupDetailResponse type:
  // https://faro01.atlassian.net/browse/ST-4705
  if (isGroupDetails(group) && group.projects !== undefined) {
    return group.projects.length;
  }

  return group.numProjects ?? 0;
}

/**
 * Gets the member count for a group
 * @param group - The group to get the member count from
 * @returns The number of members in the group, or -1 if the member count cannot be determined
 * @remarks
 * - For GroupSummaryResponse: returns the numManagers property if available
 * - For GroupDetailResponse: returns the length of the members array
 * - Returns -1 if the member count cannot be determined (properties not found or undefined)
 */
export function getMemberCount(group: GroupTypes): number {
  if ("numManagers" in group && group.numManagers !== undefined) {
    return group.numManagers;
  }
  if ("members" in group && Array.isArray(group.members)) {
    return group.members.length;
  }
  return -1;
}

/**
 * Sorts an array of groups based on the provided sorting criteria
 * @param groups - The array of groups to sort
 * @param sortBy - The sorting criteria
 * @returns A new sorted array of groups
 */
export function sortGroups(
  groups: GroupTypes[],
  sortBy: GroupsSortBy
): GroupTypes[] {
  const sortedGroups = [...groups];

  // Pre-compute timestamps for creation date sorting to avoid repeated parsing
  const createdAtMap =
    sortBy === "created_date_ascending" || sortBy === "created_date_descending"
      ? new Map(
          groups.map((group) => [group.id, new Date(group.createdAt).getTime()])
        )
      : null;

  sortedGroups.sort((a, b) => {
    switch (sortBy) {
      case "name_ascending":
        return a.name.localeCompare(b.name);
      case "name_descending":
        return b.name.localeCompare(a.name);
      case "members_ascending": {
        const aMemberCount = getMemberCount(a);
        const bMemberCount = getMemberCount(b);
        return aMemberCount - bMemberCount;
      }
      case "members_descending": {
        const aMemberCount = getMemberCount(a);
        const bMemberCount = getMemberCount(b);
        return bMemberCount - aMemberCount;
      }
      case "projects_ascending": {
        const aProjectCount = getProjectNumbersInGroup(a);
        const bProjectCount = getProjectNumbersInGroup(b);
        return aProjectCount - bProjectCount;
      }
      case "projects_descending": {
        const aProjectCount = getProjectNumbersInGroup(a);
        const bProjectCount = getProjectNumbersInGroup(b);
        return bProjectCount - aProjectCount;
      }
      case "created_date_ascending":
        return createdAtMap!.get(a.id)! - createdAtMap!.get(b.id)!;
      case "created_date_descending":
        return createdAtMap!.get(b.id)! - createdAtMap!.get(a.id)!;
      default:
        return 0;
    }
  });

  return sortedGroups;
}

/**
 * Converts a SortItem to a GroupsSortBy string
 * @param sortItem The sort item containing field and sort direction
 * @returns The GroupsSortBy string
 */
export function getGroupsSortBy(
  sortItem: SortItem<GroupsSortField>
): GroupsSortBy {
  const { field, sort } = sortItem;
  const isAscending = sort === "asc";

  // Map the field values to the GroupsSortBy keys
  switch (field) {
    case "name":
      return isAscending ? "name_ascending" : "name_descending";
    case "members":
      return isAscending ? "members_ascending" : "members_descending";
    case "projects":
      return isAscending ? "projects_ascending" : "projects_descending";
    case "createdDate":
      return isAscending ? "created_date_ascending" : "created_date_descending";
    default: {
      return DEFAULT_GROUPS_SORT_BY;
    }
  }
}
